import type { MaybeRefOrGetter } from 'vue'
import type { ContentType } from '~/models/Content/ContentType'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useRouter } from 'vue-router'
import { computed, toValue } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import useProductStore from '~/stores/product'
import useAuthStore from '~/stores/auth'
import useSearchHelper from '~/composables/useSearchHelper'
import useProductHelper from '~/composables/useProductHelper'
import useContentApi from '~/api/contentApi'

const START_PATH_DEPTH = 5

const PROTECTED_LOCATION_IDS = [
  160162, // Biblioteket: Tema
  253703, // Bibliotekt: Alle bøker
]

export default <T extends BaseItem>(
  item: MaybeRefOrGetter<BaseItem | undefined>,
  contentTypes: ContentType[],
  priorityTypes: ContentType[] = [],
) => {
  const router = useRouter()

  const { findContents } = useContentApi()
  const { cardFields } = useSearchHelper()
  const { isAuthenticated } = storeToRefs(useAuthStore())
  const { isExams, isAddonProduct } = useProductHelper()
  const { getProductFromResource } = useProductStore()

  const itemRef = computed(() => toValue(item))

  /**
   * Finds possible header Ids in path by excluding
   *
   * - Itself
   * - Start depth (eg. "/fagpakker/norsk/")
   * - Certain protected IDs
   *
   */
  const pathLocationIds = computed((): number[] => {
    if (!itemRef.value?.locationId)
      return []
    return itemRef.value.pathString.split('/')
      .slice(START_PATH_DEPTH)
      .map((id: string) => Number(id))
      .filter((id: number) => id && itemRef.value
        && id !== itemRef.value.locationId
        && !PROTECTED_LOCATION_IDS.includes(id)
      )
  })

  const isFavoriteNavigation = computed(() => !!router.currentRoute.value.params?.listId)

  /**
   * Fetches all headers below the parent product sorted by path
   */
  const { data, isLoading } = useQuery({
    enabled: computed(() => isAuthenticated.value && !isFavoriteNavigation.value && pathLocationIds.value.length > 0),
    queryKey: computed(() => ['close-button-headers', pathLocationIds.value]),
    queryFn: () => findContents<T>({
      locationIdCriterion: pathLocationIds.value,
      contentTypeCriterion: [...contentTypes, ...priorityTypes],
      sortField: 'path',
      sortOrder: 'asc',
    }, pathLocationIds.value.length, 0, cardFields),
    staleTime: Infinity
  })

  const headers = computed(() => data?.value ?? [])

  /**
   * Example of a subject headers:
   *
   * - Samfunnsfag (product)
   *   - Identitet (header)  valid
   *     - Content 1
   *     - Content 2
   *     - På tavla (header)
   *       - Content 3
   *       - Content 4
   *       - Presentasjoner (header) valid
   *         - Content 5
   *         - Content 6
   *         - Ekstra ressurser (header)
   *           - Content 7
   *
   * Example of product headers:
   *
   * - Papir+ (product) valid
   *   - Samfunnsfag (header)
   *     - Content 1
   *     - Content 2
   *     - Familien (header) valid
   *       - Content 3
   *       - Nedlastinger (header)
   *         - Content 4
   *
   * Every even subject header is valid, and every odd product header is valid
   */
  const headerViews = computed(() => headers.value
    .filter((header, index) => {
      const product = getProductFromResource(header)
      const evenOrOdd = product && isAddonProduct(product) && !isExams(product) ? 0 : 1
      return index % 2 === evenOrOdd
    })
    .reverse())

  /**
   * Always go to certain priority headers
   */
  const priorityHeader = computed(() => [...headers.value]
    .reverse()
    .find(header => priorityTypes.includes(header.contentTypeIdentifier))
  )

  return {
    headers,
    headerViews,
    priorityHeader,
    isLoading,
  }
}
